import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "../auth/auth.selector";
import { Col, Container, Row } from "react-bootstrap";
import PageHeadingNew from "../common-components/custom-comps/PageHeadingNew";
import TrainersRatingLogs from "./TrainersRatingLogs";
import TrainersRatingStats from "./TrainersRatingStats";
import CmnFilters from "../lead-management/common/CmnFilters";
import { fetchTrainerRatingsData, fetchTrainerRatingsStatData } from "../../redux/website-reducers/websiteSlice";

const TrainersRating = () => {
  const dispatch = useDispatch();
  const authData = useSelector(selectAuth);
  const userInfoData = authData.user;

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isTrainer = userInfoData?.account_type === "trainer";

  // Real stat-card data from ptTrial/rating/stats (getRatingStats).
  // Response shape: { success, data: [ { total_members, total_ratings, ... } ] }
  const { trainerRatingsStat } = useSelector((state) => state?.webApi);
  const statsRow = Array.isArray(trainerRatingsStat?.data)
    ? trainerRatingsStat.data[0]
    : trainerRatingsStat?.data;
  const statsData = statsRow
    ? {
        total_member: statsRow.total_members ?? 0,
        total_rating: statsRow.total_ratings ?? 0,
        total_gym: statsRow.total_gyms ?? 0,
        total_trainer: statsRow.total_trainers ?? 0,
        three_day_trial: statsRow.threeday_count ?? 0,
        regular_ratings: statsRow.regular_count ?? 0,
        pt_trial: statsRow.pt_count ?? 0,
      }
    : null;

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const iniAllFilter = {
    selectedGym: !isAdmin && !isMarketing
      ? {
          label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
          value:
            userInfoData?.gym_id ||
            userInfoData?.user_id ||
            userInfoData?.uid ||
            "",
        }
      : "",
    selectedTrainer: isTrainer
      ? {
          label: userInfoData?.name,
          value: userInfoData?.uid || userInfoData?.user_id,
        }
      : "",
    selectedRating: "",
    selectedRatingType: "",
    leadAddDateFrom: "",
    leadAddDateTo: "",
    showFiltersDropdown: false,
  };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  
  const iniFilters = {
    page: isPage + 1,
    limit: pageLimit || 10,
    gym_id: allFilters.selectedGym?.value || "",
    trainer_id: allFilters.selectedTrainer?.value || "",
    rating: allFilters.selectedRating?.value || "",
    rating_type: allFilters.selectedRatingType?.value || "",
    from_date: allFilters.leadAddDateFrom || "",
    to_date: allFilters.leadAddDateTo || "",
  };
  const [filters, setFilters] = useState(iniFilters);
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);
  
  // Data state for the table
  const iniDatas = {
    pagination: [],
    allData: [],
  };
  const [allDatas, setAllDatas] = useState(iniDatas);

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setFilters(iniFilters);
    setSearchFilterApplied(false);
  };

  const onClickSearch = () => {
    setIsPage(0);

    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prevs) => ({ ...prevs, search: allFilters.searchString }));
    }
  };

  // Clicking a stat card filters the table by rating_type.
  // Aggregate cards (total_*) clear the filter (show all).
  const cardKeyToRatingType = {
    three_day_trial: "threeday",
    pt_trial: "pt",
    regular_ratings: "regular",
  };
  const onStatCardClick = (key) => {
    const ratingType = cardKeyToRatingType[key] || "";
    setIsPage(0);
    setAllFilters((prevs) => ({
      ...prevs,
      selectedRatingType: ratingType
        ? { label: key.replace(/_/g, " "), value: ratingType }
        : "",
    }));
  };

  const getTrainerRatingsData = useCallback((filters) => {
    dispatch(fetchTrainerRatingsData({
      filters: filters,
      token: userInfoData?.token,
    }));
  }, [dispatch, userInfoData?.token]);

  useEffect(() => {
    getTrainerRatingsData(filters);
  }, [filters, getTrainerRatingsData]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      page: isPage + 1,
      limit: pageLimit || 10,
      gym_id: allFilters.selectedGym?.value || "",
      trainer_id: allFilters.selectedTrainer?.value || "",
      rating: allFilters.selectedRating?.value || "",
      rating_type: allFilters.selectedRatingType?.value || "",
      from_date: allFilters.leadAddDateFrom || "",
      to_date: allFilters.leadAddDateTo || "",
    }));
  }, [
    isPage,
    pageLimit,
    allFilters.selectedGym,
    allFilters.selectedTrainer,
    allFilters.selectedRating,
    allFilters.selectedRatingType,
    allFilters.leadAddDateFrom,
    allFilters.leadAddDateTo,
  ]);

  useEffect(()=>{
   dispatch(fetchTrainerRatingsStatData(userInfoData?.token))
  },[userInfoData?.token])


  useEffect(() => {
    if (allFilters.searchString?.length === 0) {
      setSearchFilterApplied(false);
      setFilters((prevs) => ({ ...prevs, search: "" }));
      setIsPage(0);
    }
  }, [allFilters.searchString]);

  useEffect(() => {
    setAllFilters((prevs) => ({
      ...prevs,
      selectedGym: !isAdmin && !isMarketing
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    }));
  }, [isAdmin, isGym, isMarketing, userInfoData?.gym_id, userInfoData?.gym_name, userInfoData?.name, userInfoData?.uid, userInfoData?.user_id]);

  return (
    <Container className="bg-cloud-k px-50px" fluid>
      <Row>
        <Col>
          <PageHeadingNew
            heading="Trainers Rating"
            subheading="Monitor and manage trainer ratings and feedback"
            onReload={() => {
              getTrainerRatingsData(filters);
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <TrainersRatingStats
            statsData={statsData}
            onCardClick={onStatCardClick}
            activeRatingType={allFilters.selectedRatingType?.value || ""}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <CmnFilters
            from="trainers-rating"
            userInfoData={userInfoData}
            filters={allFilters}
            setFilters={setAllFilters}
            onClickReset={onResetFilters}
            onClickSearch={onClickSearch}
            searchFilterApplied={searchFilterApplied}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <TrainersRatingLogs
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            allDatas={allDatas}
            setAllDatas={setAllDatas}
            userInfoData={userInfoData}
            callFunction={() => {
              getTrainerRatingsData(filters);
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default TrainersRating;
